'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Supplier, SupplierInput } from '@garage/shared'
import { supplierInputSchema } from '@garage/shared'
import { Dialog, TextField, Switch, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { ActivityInfo } from '@/components/ActivityInfo.js'

interface SupplierFormDialogProps {
  open: boolean
  supplier?: Supplier
  onClose: () => void
}

interface FormState {
  name: string
  location: string
  phone: string
  /** Comma-separated for editing; split into tags on save. */
  carBrandsText: string
  suppliesCategoriesText: string
  offersDelivery: boolean
  notes: string | null
}

/** "BMW, Toyota,  , mercedes" -> ["BMW", "Toyota", "mercedes"]. Real dedup/trim happens server-side too. */
function splitTags(text: string): string[] {
  return text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
}

export function SupplierFormDialog({ open, supplier, onClose }: SupplierFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const isCreate = !supplier

  const initialState = (): FormState => ({
    name: supplier?.name ?? '',
    location: supplier?.location ?? '',
    phone: supplier?.phone ?? '',
    carBrandsText: supplier?.carBrands.join(', ') ?? '',
    suppliesCategoriesText: supplier?.suppliesCategories.join(', ') ?? '',
    offersDelivery: supplier?.offersDelivery ?? false,
    notes: supplier?.notes ?? null,
  })

  const [formState, setFormState] = useState<FormState>(initialState)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState(initialState())
      setFieldErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supplier])

  const mutation = useMutation({
    mutationFn: async (input: SupplierInput) =>
      isCreate ? api.suppliers.create(input) : api.suppliers.update(supplier!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.suppliers() })
      if (!isCreate) queryClient.invalidateQueries({ queryKey: qk.supplier(supplier!.id) })
      snackbar.success(isCreate ? t('supplierForm.supplierAdded') : t('supplierForm.supplierUpdated'))
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const input: SupplierInput = {
      name: formState.name,
      location: formState.location,
      phone: formState.phone,
      carBrands: splitTags(formState.carBrandsText),
      suppliesCategories: splitTags(formState.suppliesCategoriesText),
      offersDelivery: formState.offersDelivery,
      notes: formState.notes,
    }

    const result = supplierInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('supplierForm.addSupplier') : t('supplierForm.editSupplier')}
      size="md"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isCreate && supplier && <ActivityInfo entityType="supplier" entityId={supplier.id} />}
        <TextField
          label={t('supplierForm.shopName')}
          value={formState.name}
          onChange={(v) => setFormState((s) => ({ ...s, name: v }))}
          error={fieldErrors.name}
          fullWidth
          autoFocus
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label={t('supplierForm.location')}
            value={formState.location}
            onChange={(v) => setFormState((s) => ({ ...s, location: v }))}
            error={fieldErrors.location}
            placeholder={t('supplierForm.locationPlaceholder')}
            supportingText={t('supplierForm.locationHelp')}
            fullWidth
          />
          <TextField
            label={t('supplierForm.phone')}
            value={formState.phone}
            onChange={(v) => setFormState((s) => ({ ...s, phone: v }))}
            error={fieldErrors.phone}
            fullWidth
          />
        </div>

        <TextField
          label={t('supplierForm.carBrands')}
          value={formState.carBrandsText}
          onChange={(v) => setFormState((s) => ({ ...s, carBrandsText: v }))}
          error={fieldErrors.carBrands}
          placeholder={t('supplierForm.carBrandsPlaceholder')}
          supportingText={t('supplierForm.tagsHelp')}
          fullWidth
        />

        <TextField
          label={t('supplierForm.suppliesCategories')}
          value={formState.suppliesCategoriesText}
          onChange={(v) => setFormState((s) => ({ ...s, suppliesCategoriesText: v }))}
          error={fieldErrors.suppliesCategories}
          placeholder={t('supplierForm.suppliesCategoriesPlaceholder')}
          supportingText={t('supplierForm.tagsHelp')}
          fullWidth
        />

        <Switch
          checked={formState.offersDelivery}
          onChange={(v) => setFormState((s) => ({ ...s, offersDelivery: v }))}
          label={t('supplierForm.offersDelivery')}
        />

        <TextField
          label={t('clientDetail.notes')}
          value={formState.notes ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, notes: v || null }))}
          error={fieldErrors.notes}
          multiline
          rows={2}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
